import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { AdminSubNav, CHATBOT_TABS } from "@/components/admin/AdminSubNav";
import {
  listKbDocuments,
  ingestKbUrl,
  ingestKbFile,
  ingestKbText,
  setKbDocumentEnabled,
  deleteKbDocument,
  syncGuidelinesToKb,
  type KbDocumentRow,
} from "@/lib/kb.functions";
import { Loader2, Trash2, RefreshCw, Upload, Link2, FileText } from "lucide-react";

export const Route = createFileRoute("/admin/chatbot/knowledge")({
  head: () => ({
    meta: [
      { title: "Knowledge base · Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ChatbotKnowledge,
});

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Could not read that file"));
    reader.readAsDataURL(file);
  });
}

function ChatbotKnowledge() {
  const listFn = useServerFn(listKbDocuments);
  const urlFn = useServerFn(ingestKbUrl);
  const fileFn = useServerFn(ingestKbFile);
  const textFn = useServerFn(ingestKbText);
  const toggleFn = useServerFn(setKbDocumentEnabled);
  const deleteFn = useServerFn(deleteKbDocument);
  const syncFn = useServerFn(syncGuidelinesToKb);

  const [rows, setRows] = useState<KbDocumentRow[] | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");
  const [noteTitle, setNoteTitle] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const fileRef = useRef<HTMLInputElement | null>(null);

  const refresh = useCallback(() => {
    listFn()
      .then(setRows)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [listFn]);

  useEffect(refresh, [refresh]);

  async function run(label: string, fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    setStatus(`${label}…`);
    try {
      await fn();
      setStatus(`${label} — done.`);
      refresh();
    } catch (e) {
      setStatus(null);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onFiles(files: FileList | null) {
    if (!files?.length) return;
    const list = Array.from(files);
    await run(`Indexing ${list.length} file(s)`, async () => {
      for (const file of list) {
        const base64 = await fileToBase64(file);
        await fileFn({
          data: { filename: file.name, base64, mimeType: file.type, title: file.name },
        });
      }
    });
    if (fileRef.current) fileRef.current.value = "";
  }

  return (
    <div>
      <AdminSubNav tabs={CHATBOT_TABS} />
      <div className="space-y-6 p-4 sm:p-6">
        <header className="space-y-1">
          <h1 className="text-2xl font-bold">Knowledge base</h1>
          <p className="text-sm text-muted-foreground">
            Everything SAM is allowed to answer from. Upload guideline PDFs, spreadsheets, Word
            docs, or paste a URL — the assistant searches this library on every question.
          </p>
        </header>

        {status && <p className="rounded-md bg-muted px-3 py-2 text-sm">{status}</p>}
        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="grid gap-4 lg:grid-cols-3">
          <section className="rounded-xl border border-border p-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <Upload className="h-4 w-4" /> Upload files
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              PDF, Word (.docx), Excel (.xlsx/.csv) or plain text.
            </p>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.md"
              disabled={busy}
              onChange={(e) => void onFiles(e.target.files)}
              className="mt-3 w-full text-sm"
            />
          </section>

          <section className="rounded-xl border border-border p-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <Link2 className="h-4 w-4" /> Add a URL
            </h2>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…"
              className="mt-3 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <button
              type="button"
              disabled={busy || !url.trim()}
              onClick={() =>
                void run("Indexing page", async () => {
                  await urlFn({ data: { url } });
                  setUrl("");
                })
              }
              className="mt-3 w-full rounded-md bg-brand px-3 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              Index page
            </button>
          </section>

          <section className="rounded-xl border border-border p-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <RefreshCw className="h-4 w-4" /> Lending guidelines
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Push every internal program matrix into SAM's searchable memory. Wholesale lender
              names are stripped automatically.
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => void run("Syncing guidelines", () => syncFn())}
              className="mt-3 w-full rounded-md border border-brand px-3 py-2 text-sm font-semibold text-brand disabled:opacity-50"
            >
              Sync guidelines now
            </button>
          </section>
        </div>

        <section className="rounded-xl border border-border p-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <FileText className="h-4 w-4" /> Paste a note
          </h2>
          <div className="mt-3 grid gap-2 sm:grid-cols-[240px_1fr]">
            <input
              value={noteTitle}
              onChange={(e) => setNoteTitle(e.target.value)}
              placeholder="Title"
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <textarea
              value={noteBody}
              onChange={(e) => setNoteBody(e.target.value)}
              rows={4}
              placeholder="Program notes, overlays, FAQs…"
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
          <button
            type="button"
            disabled={busy || !noteTitle.trim() || noteBody.trim().length < 40}
            onClick={() =>
              void run("Saving note", async () => {
                await textFn({ data: { title: noteTitle, content: noteBody } });
                setNoteTitle("");
                setNoteBody("");
              })
            }
            className="mt-3 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            Add to knowledge base
          </button>
        </section>

        <section className="rounded-xl border border-border">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h2 className="text-sm font-semibold">Indexed sources</h2>
            <button type="button" onClick={refresh} className="text-xs text-brand underline">
              Refresh
            </button>
          </div>
          {!rows ? (
            <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">Nothing indexed yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {rows.map((doc) => (
                <li key={doc.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{doc.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {doc.source_type} · {doc.chunk_count} passages ·{" "}
                      {doc.status === "error" ? (
                        <span className="text-destructive">{doc.error ?? "error"}</span>
                      ) : (
                        doc.status
                      )}
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={doc.enabled}
                      onChange={(e) =>
                        void run("Updating source", () =>
                          toggleFn({ data: { id: doc.id, enabled: e.target.checked } }),
                        )
                      }
                    />
                    Active
                  </label>
                  <button
                    type="button"
                    aria-label="Delete source"
                    onClick={() =>
                      void run("Deleting source", () => deleteFn({ data: { id: doc.id } }))
                    }
                    className="rounded p-1 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
